import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F4F5FE]">
      {/* Header */}
      <header className="bg-[#0A0A12] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src="/logo-fascinai.png" alt="FASCINAI" className="h-8 w-auto" />
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-white/60 hover:text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12">
          <h1 className="text-3xl font-bold text-[#1A1C2E] mb-2">Política de Privacidad</h1>
          <p className="text-gray-500 mb-8">Última actualización: 8 de marzo de 2026</p>

          <div className="prose prose-gray max-w-none space-y-6 text-gray-600">
            <p>
              Queremos invitarte personalmente a leer esta política en donde obtendrás información completa y transparente sobre cómo se recaban tus datos en esta web, la finalidad con la que se utiliza esa información, los sistemas de captura así como las herramientas que utilizo para gestionar esa información.
            </p>

            <p>Esta política define las condiciones establecidas para el siguiente dominio:</p>
            <p className="font-semibold text-[#4E62F4]">https://fascinai.pro</p>

            <p>
              Dicho dominio es responsabilidad de <strong>FASCINADS SOLUCIONES SL</strong> que obra también como responsable de toda la información recabada mediante dicho enlace.
            </p>

            <p>
              Te animo a leer detenidamente estos términos antes de facilitar tus datos personales en esta web. Los mayores de trece años podrán registrarse en fascinai.pro como usuarios sin el previo consentimiento de sus padres o tutores.
            </p>

            <p>
              En el caso de los menores de trece años se requiere el consentimiento de los padres o tutores para el tratamiento de sus datos personales.
            </p>

            <p>
              Si eres menor de trece años y has accedido a este sitio web sin avisar a tus padres no debes registrarte como usuario.
            </p>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">Nuestros Principios</h2>
            <p>En esta web se respetan y cuidan los datos personales de los usuarios. Como usuario debes saber que tus derechos están garantizados.</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Nunca solicitamos información personal a menos que realmente sea necesaria para prestarte los servicios que nos requieras.</li>
              <li>Nunca compartimos información personal de nuestros usuarios con nadie, excepto para cumplir con la ley o en caso que cuente con tu autorización expresa.</li>
              <li>Nunca utilizamos tus datos personales con una finalidad diferente a la expresada en esta política de privacidad.</li>
            </ul>

            <p>
              Es preciso advertir que esta Política de Privacidad podría variar en función de exigencias legislativas o de autorregulación, por lo que se aconseja a los usuarios que la visiten periódicamente.
            </p>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">Responsable del Tratamiento</h2>
            <ul className="list-none space-y-2">
              <li><strong>Identidad del Responsable:</strong> FASCINADS SOLUCIONES SL</li>
              <li><strong>NIF/CIF:</strong> B70880182</li>
              <li><strong>Dirección:</strong> Juez José Bernabeu Rocamora, 31 - 03348 Granja de Rocamora (Alicante)</li>
              <li><strong>Correo electrónico:</strong> soporte@fascinai.pro</li>
            </ul>

            <p>
              FASCINADS SOLUCIONES SL ha adecuado esta web a las exigencias de la Ley Orgánica 15/1999, de 13 de diciembre, de Protección de Datos de Carácter Personal (LOPD), y al Real Decreto 1720/2007, de 21 de diciembre. Cumple también con el Reglamento (UE) 2016/679 del Parlamento Europeo y del Consejo de 27 de abril de 2016 relativo a la protección de las personas físicas (RGPD), así como con la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y Comercio Electrónico (LSSI).
            </p>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">Principios Aplicados</h2>
            <p>En el tratamiento de tus datos personales, aplicaremos los siguientes principios:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Licitud, lealtad y transparencia:</strong> Siempre requeriremos tu consentimiento para el tratamiento de tus datos personales con absoluta transparencia.</li>
              <li><strong>Minimización de datos:</strong> Solo solicitamos datos estrictamente necesarios.</li>
              <li><strong>Limitación del plazo de conservación:</strong> Los datos serán mantenidos durante no más tiempo del necesario.</li>
              <li><strong>Integridad y confidencialidad:</strong> Tus datos serán tratados con seguridad adecuada y confidencialidad.</li>
            </ul>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">¿Cómo Hemos Obtenido Tus Datos?</h2>
            <p>Los datos personales que tratamos en fascinai.pro proceden de:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Formulario de registro</li>
              <li>Formulario de contacto</li>
              <li>Formulario de suscripción</li>
              <li>Sistema de chat online</li>
              <li>Proceso de checkout y pago</li>
            </ul>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">Tus Derechos</h2>
            <p>Cualquier persona tiene derecho a obtener confirmación sobre si en fascinai.pro estamos tratando datos personales o no.</p>
            <p>Las personas interesadas tienen derecho a:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Solicitar el acceso a los datos personales</li>
              <li>Solicitar su rectificación o supresión</li>
              <li>Solicitar la limitación de su tratamiento</li>
              <li>Oponerse al tratamiento</li>
              <li>Solicitar la portabilidad de los datos</li>
            </ul>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">Finalidad del Tratamiento</h2>
            <p>Cuando un usuario se registra en FASCINAI ONE, está facilitando información de carácter personal que incluye:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Nombre y apellidos:</strong> Para identificación y personalización del servicio</li>
              <li><strong>Email:</strong> Para comunicaciones, acceso a la plataforma y envío de facturas</li>
              <li><strong>Información de pago:</strong> Procesada por Stripe (no almacenamos datos de tarjeta)</li>
              <li><strong>Datos de uso:</strong> Para mejorar el servicio y análisis de rendimiento</li>
            </ul>

            <p><strong>Formulario de Registro:</strong> Solicitamos nombre, apellidos y email para crear tu cuenta y dar acceso a las funcionalidades de la plataforma.</p>
            <p><strong>Procesamiento de pagos:</strong> Los datos de pago son procesados por Stripe. FASCINAI ONE no almacena ni tiene acceso a información completa de tarjetas de crédito.</p>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">Servicios de Terceros</h2>
            <p>Te informamos que los datos que facilitas pueden estar ubicados en servidores de:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Stripe:</strong> Procesamiento de pagos (fuera de la UE, en EEUU, acogido a Privacy Shield)</li>
              <li><strong>Google Analytics:</strong> Análisis web (fuera de la UE, nivel de protección adecuado según Comisión UE)</li>
              <li><strong>Postmark:</strong> Envío de emails transaccionales (dentro de la UE)</li>
              <li><strong>Hosting:</strong> Servidores dentro de la UE</li>
            </ul>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">Legitimación</h2>
            <p>La base legal para el tratamiento de tus datos es el consentimiento.</p>
            <p>Para registrarse y usar FASCINAI ONE se requiere el consentimiento con esta política de privacidad.</p>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">Conservación de Datos</h2>
            <p>Los datos personales proporcionados se conservarán hasta que no se solicite su supresión por el interesado.</p>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">Seguridad</h2>
            <p>fascinai.pro se compromete en el uso y tratamiento de los datos personales de los usuarios, respetando su confidencialidad y utilizándolos de acuerdo con la finalidad del mismo.</p>
            <p>Esta web incluye un certificado SSL para que tus datos viajen de manera íntegra y segura.</p>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">Exactitud de los Datos</h2>
            <p>Como usuario, eres el único responsable de la veracidad y corrección de los datos que remitas a FASCINADS SOLUCIONES SL.</p>
            <p>Los usuarios garantizan y responden de la exactitud, vigencia y autenticidad de los datos personales facilitados.</p>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">Aceptación y Consentimiento</h2>
            <p>El usuario declara haber sido informado de las condiciones sobre protección de datos de carácter personal, aceptando y consintiendo el tratamiento de los mismos por parte de FASCINADS SOLUCIONES SL.</p>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">Cambios en la Política</h2>
            <p>FASCINADS SOLUCIONES SL se reserva el derecho a modificar la presente política para adaptarla a novedades legislativas. Los cambios se anunciarán con antelación razonable.</p>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">Contacto</h2>
            <p>Para cualquier consulta sobre esta política de privacidad o el ejercicio de tus derechos, puedes contactarnos en:</p>
            <ul className="list-none space-y-2">
              <li><strong>Email:</strong> soporte@fascinai.pro</li>
              <li><strong>Dirección:</strong> Juez José Bernabeu Rocamora, 31 - 03348 Granja de Rocamora (Alicante)</li>
            </ul>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#0A0A12] py-8 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs text-white/30">© 2026 Fascinads Soluciones SL</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
